/**
 * Shared hook to check if the reminder automation is enabled for an org.
 *
 * Used by the calendar (to hide reminders when disabled) and the dashboard
 * (to hide the reminders section).
 */

import { useQuery } from '@tanstack/react-query';

import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';

export const REMINDER_AUTOMATION_QUERY_KEY = 'reminder-automation-enabled';

export function useReminderAutomationEnabled(
  orgId: number | undefined
): boolean {
  const { data, isPending, isError } = useQuery({
    queryKey: ['crm', orgId, REMINDER_AUTOMATION_QUERY_KEY],
    queryFn: async () => {
      if (!orgId) {
        return false;
      }
      const client = createCRMObjectClient(orgId, 'ahoy_event_automation');
      // Filter server-side by automation name so the result is independent of
      // how many other automations exist. Scanning a single page client-side
      // would return false negatives once an org has > page-size automations.
      const resp = await client.list({
        filter_groups: [
          {
            filters: [
              {
                property: 'ahoy_automation_name',
                operator: 'exact',
                value: 'reminder_from_date_properties',
              },
            ],
          },
        ],
        limit: 1,
      });
      const automation = resp.results?.[0];
      if (!automation) {
        return false;
      }
      return (
        (automation.properties?.ahoy_enabled as { value?: string } | undefined)
          ?.value === 'true'
      );
    },
    enabled: !!orgId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
  // Without an org context (e.g. dashboards rendered before an org loads),
  // there is nothing to query — hide reminders.
  if (!orgId) {
    return false;
  }
  // While the query is in-flight or has errored, default to true so reminders
  // are not hidden on transient failures. Only return false once the query has
  // confirmed the automation is missing or disabled.
  if (isPending || isError) {
    return true;
  }
  return data ?? false;
}
